import styled, { DefaultTheme } from 'styled-components';

type RuleVariant = 'block' | 'allow';

const getRuleColor = (theme: DefaultTheme, variant: RuleVariant) => (
  variant === 'block' ? theme.colors.negative : theme.colors.positive
);

export const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  min-width: min(760px, 78vw);
  color: ${(props) => props.theme.colors.onSurface};

  @media (max-width: 720px) {
    min-width: 0;
  }
`;

export const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  padding: 0.35rem 0;
`;

export const SectionTitle = styled.h3`
  margin: 0;
  color: ${(props) => props.theme.colors.onSurface};
  font-size: 0.96rem;
  font-weight: 700;
`;

export const FieldLabel = styled.label`
  display: block;
  margin-bottom: 0.35rem;
  color: ${(props) => props.theme.colors.onSurface};
  font-size: 0.82rem;
  font-weight: 600;
`;

export const Field = styled.div`
  min-width: 0;
`;

export const RuleType = styled.strong<{ $isBlock: boolean }>`
  color: ${(props) => (props.$isBlock ? props.theme.colors.negative : props.theme.colors.positive)};
  font-weight: 700;
`;

export const FieldsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.8rem;

  @media (max-width: 620px) {
    grid-template-columns: 1fr;
  }
`;

export const ChoiceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.7rem;

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

export const ChoiceButton = styled.button<{ $selected: boolean; $variant: RuleVariant }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.15rem;
  min-height: 4.2rem;
  padding: 0.75rem 0.9rem;
  border: 1px solid ${(props) => (props.$selected
    ? getRuleColor(props.theme, props.$variant)
    : `${props.theme.colors.onBackground}40`)};
  border-radius: 0.7rem;
  background: ${(props) => (props.$selected
    ? `${getRuleColor(props.theme, props.$variant)}16`
    : props.theme.colors.surface)};
  color: ${(props) => props.theme.colors.onSurface};
  cursor: pointer;
  text-align: left;
  transition: 0.18s ease;

  &:hover {
    border-color: ${(props) => getRuleColor(props.theme, props.$variant)};
  }

  &:focus-visible {
    outline: 2px solid ${(props) => getRuleColor(props.theme, props.$variant)};
    outline-offset: 2px;
  }

  strong {
    color: ${(props) => (props.$selected
    ? getRuleColor(props.theme, props.$variant)
    : props.theme.colors.onSurface)};
    font-size: 0.9rem;
  }
`;

export const ChoiceHeader = styled.span`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const ChoiceIndicator = styled.span<{ $selected: boolean; $variant: RuleVariant }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 1rem;
  height: 1rem;
  border: 1px solid ${(props) => (props.$selected
    ? getRuleColor(props.theme, props.$variant)
    : `${props.theme.colors.onBackground}70`)};
  border-radius: 0.25rem;
  background: ${(props) => (props.$selected
    ? getRuleColor(props.theme, props.$variant)
    : props.theme.colors.surface)};
  color: ${(props) => props.theme.colors.surface};
  font-size: 0.72rem;
  font-weight: 700;
  line-height: 1;
`;

export const ChoiceDescription = styled.span`
  color: ${(props) => props.theme.colors.onSurface}b0;
  font-size: 0.75rem;
`;

export const CheckboxGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.7rem;

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

export const CheckboxCard = styled.label<{ $selected: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  padding: 0.65rem 0.8rem;
  border: 1px solid transparent;
  border-radius: 0.5rem;
  background: ${(props) => (props.$selected ? `${props.theme.colors.primary}12` : 'transparent')};
  cursor: pointer;
  font-size: 0.86rem;

  input {
    flex: 0 0 auto;
    margin-top: 0.18rem;
  }

  &:hover {
    background: ${(props) => `${props.theme.colors.primary}08`};
  }
`;

export const ActionCopy = styled.span`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;

  strong {
    color: ${(props) => props.theme.colors.onSurface};
    font-size: 0.86rem;
    font-weight: 600;
  }

  small {
    color: ${(props) => `${props.theme.colors.onSurface}a0`};
    font-size: 0.73rem;
    line-height: 1.35;
  }
`;

export const WeekdayList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
`;

export const WeekdayButton = styled.button<{ $selected: boolean }>`
  min-width: 4.25rem;
  padding: 0.5rem 0.6rem;
  border: 1px solid
    ${(props) => (props.$selected ? props.theme.colors.primary : `${props.theme.colors.onBackground}40`)};
  border-radius: 999px;
  background: ${(props) => (props.$selected ? props.theme.colors.primary : props.theme.colors.surface)};
  color: ${(props) => (props.$selected ? props.theme.colors.onPrimary : props.theme.colors.onSurface)};
  cursor: pointer;
  font-size: 0.78rem;
  transition: 0.18s ease;

  &:hover {
    border-color: ${(props) => props.theme.colors.primary};
  }
`;

export const NativeSelect = styled.select`
  width: 100%;
  min-height: 2.1rem;
  padding: 0.35rem 0.55rem;
  border: 1px solid ${(props) => props.theme.colors.onBackground}50;
  border-radius: 0.35rem;
  background: ${(props) => props.theme.colors.surface};
  color: ${(props) => props.theme.colors.onSurface};
`;

export const AudienceRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: end;
  gap: 0.7rem;

  @media (max-width: 620px) {
    grid-template-columns: 1fr;
  }
`;

export const Hint = styled.p`
  margin: 0;
  color: ${(props) => props.theme.colors.onSurface}a0;
  font-size: 0.75rem;
`;

export const InlineCheckbox = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  width: fit-content;
  color: ${(props) => props.theme.colors.onSurface};
  font-size: 0.84rem;
  cursor: pointer;
`;
